import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../../contexts/LanguageContext';
import { 
  WifiIcon, 
  SignalSlashIcon, 
  ExclamationTriangleIcon,
  CheckCircleIcon,
  ArrowPathIcon
} from '@heroicons/react/24/outline';

interface NetworkStatusProps {
  onRetry?: () => void;
  showWhenOnline?: boolean;
}

const NetworkStatus: React.FC<NetworkStatusProps> = ({ 
  onRetry, 
  showWhenOnline = false 
}) => {
  const { t } = useTranslation();
  const { isRTL } = useLanguage();
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [isChecking, setIsChecking] = useState(false);
  const [lastChecked, setLastChecked] = useState<Date | null>(null);

  const checkConnectivity = useCallback(async () => {
    setIsChecking(true);
    try {
      // Try to fetch a small resource to verify connectivity
      const response = await fetch('/favicon.ico', { 
        method: 'HEAD',
        cache: 'no-cache',
        signal: AbortSignal.timeout(5000)
      });
      setIsOnline(response.ok);
    } catch {
      setIsOnline(false);
    } finally {
      setIsChecking(false);
      setLastChecked(new Date());
    }
  }, []);

  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      checkConnectivity(); // Verify actual connectivity
    };

    const handleOffline = () => {
      setIsOnline(false);
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // Check connectivity on mount
    checkConnectivity();

    // Periodic connectivity check when online
    const interval = setInterval(checkConnectivity, 30000); // Every 30 seconds

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      clearInterval(interval);
    };
  }, [checkConnectivity]);

  // Don't show anything if online and showWhenOnline is false
  if (isOnline && !showWhenOnline) {
    return null;
  }

  const getStatusIcon = () => {
    if (isChecking) {
      return <ArrowPathIcon className="h-4 w-4 animate-spin" />;
    }
    
    if (isOnline) {
      return <CheckCircleIcon className="h-4 w-4 text-green-500" />;
    }
    
    return <SignalSlashIcon className="h-4 w-4 text-red-500" />;
  };

  const getStatusText = () => {
    if (isChecking) {
      return t('network.checking', 'Checking connection...');
    }
    
    if (isOnline) {
      return t('network.online', 'Connected');
    }
    
    return t('network.offline', 'No internet connection');
  };

  const getStatusColor = () => {
    if (isChecking) {
      return 'bg-blue-50 border-blue-200 text-blue-800';
    }
    
    if (isOnline) {
      return 'bg-green-50 border-green-200 text-green-800';
    }
    
    return 'bg-red-50 border-red-200 text-red-800';
  };

  const getStatusIconColor = () => {
    if (isChecking) {
      return 'text-blue-500';
    }
    
    if (isOnline) {
      return 'text-green-500';
    }
    
    return 'text-red-500';
  };

  return (
    <div 
      className={`fixed top-4 ${isRTL ? 'left-4' : 'right-4'} z-50 transition-all duration-300`}
      dir={isRTL ? 'rtl' : 'ltr'}
    >
      <div className={`
        flex items-center space-x-2 px-3 py-2 rounded-lg border shadow-sm
        ${getStatusColor()}
        ${isRTL ? 'space-x-reverse' : ''}
      `}>
        <div className={`flex items-center ${getStatusIconColor()}`}>
          {getStatusIcon()}
        </div>
        
        <div className="flex items-center space-x-2">
          <span className="text-sm font-medium">
            {getStatusText()}
          </span>
          
          {!isOnline && onRetry && (
            <button
              onClick={onRetry}
              className="text-sm underline hover:no-underline"
              disabled={isChecking}
            >
              {t('network.retry', 'Retry')}
            </button>
          )}
        </div>
        
        {lastChecked && isOnline && showWhenOnline && (
          <div className="text-xs opacity-75">
            {t('network.lastChecked', 'Last checked')}: {lastChecked.toLocaleTimeString()}
          </div>
        )}
      </div>
    </div>
  );
};

export default NetworkStatus;
